/*
 * Copyright (c) 2023 - 2026. Bolmate B.V.
 */
import './style/dashboard.scss';
import './style/todos.scss';
import {AwesomeIcon, ChangeFunction, Link, Loader} from '@dvrd/dvr-controls';
import classNames from 'classnames';
import React, {KeyboardEventHandler, MouseEventHandler, useContext, useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useBreakpoint} from '../../util/useBreakpoint';
import Invoice from '../../models/invoiceModel';
import {userHasRight} from '../../util/accessUtil';
import {hasAdminLogin} from '../../util/adminUtil';
import {getInvoiceOptions, getOrderStatusItems} from '../../util/dashboardUtil';
import {InvoiceOptionName} from '../../util/enums';
import {
    AccountStatus,
    CompleteInvoiceFilter,
    CustomerType,
    InvoiceFilter,
    InvoiceUploadStats,
    OrderStatus
} from '../../util/interfaces';
import {InvoiceOption} from '../../util/types';
import {BolmateButton, BolmateEmptyState, BolmateListGroup, BolmateMenuOption, BolmateSearchInput} from '../controls';
import PortalSelectController from '../controls/portalSelect/portalSelectController';
import {DotsLoader} from '../controls/loader/dotsLoader';
import InvoiceRequestController from '../invoice/request/invoiceRequestController';
import {AppContext} from '../misc/appContext';
import InfoBanner from '../misc/infoBanner';
import {DashboardTab} from './dashboardController';
import OrderCard from './orderCard';
import {groupInvoicesByDate, InvoiceDateGroup} from './orderGrouping';
import InvoiceFilterController, {InvoiceFilterRef} from './filter/invoiceFilterController';
import ImportStatus from './importStatus';
import UploadStatsController from './uploadStats/uploadStatsController';

interface Props {
    onClickDetails: (invoice: Invoice) => MouseEventHandler;
    onClickEdit: (invoice: Invoice) => MouseEventHandler;
    onClickMail: (invoice: Invoice) => MouseEventHandler;
    onUploadInvoice: (invoice: Invoice) => MouseEventHandler;
    onClickDownload: (invoice: Invoice) => MouseEventHandler;
    onPreviewFile: (invoice: Invoice) => MouseEventHandler;
    onChangeSearch: ChangeFunction<string>;
    onChangeStatusFilter: ChangeFunction<OrderStatus | ''>;
    onSearch: KeyboardEventHandler;
    onClickApiFlow: MouseEventHandler;
    onClickCredit: (invoice: Invoice) => MouseEventHandler;
    onOpenFilters: MouseEventHandler;
    onCloseFilters: VoidFunction;
    onSubmitFilters: (filters: CompleteInvoiceFilter) => void;
    onClickReverseCharge: (invoice: Invoice) => MouseEventHandler;
    onClickAssignInvoiceNumber: (invoice: Invoice) => MouseEventHandler;
    /** Reports a card entering the viewport so its details load lazily. */
    onCardVisible: (ix: number) => (invoiceId: string) => void;
    onCopyInvoiceID: (invoice: Invoice) => MouseEventHandler;
    onCancelEmails: (invoice: Invoice) => MouseEventHandler;
    onReload: MouseEventHandler;
    reloadNumOpen: VoidFunction;
    filtersOpen: boolean;
    /** Full size of the filtered set (across all pages), for the title badge. */
    totalInvoices: number;
    invoices: Invoice[];
    loadingInvoices: boolean;
    filter: InvoiceFilter;
    isImportingHistory: boolean;
    sendingHistory: boolean;
    tab: DashboardTab;
    uploadStats: InvoiceUploadStats;
    loadingUploadStats: boolean;
    hasAPIConnection: boolean;
    loadingMore: boolean;
}

export default function Dashboard(props: Props) {
    const {t} = useTranslation();
    const bp = useBreakpoint();
    const context = useContext(AppContext);
    const activeAccountId = context.accountContext.account?.id ?? '';
    const {
        filter,
        filtersOpen,
        onOpenFilters,
        onSubmitFilters,
        onCloseFilters,
        onClickDetails,
        onClickCredit,
        onClickMail,
        onClickEdit,
        onPreviewFile,
        onSearch,
        loadingInvoices,
        invoices,
        onChangeSearch,
        onClickDownload,
        onCancelEmails,
        onReload,
        onClickApiFlow,
        onClickReverseCharge,
        onChangeStatusFilter,
        onClickAssignInvoiceNumber,
        onCardVisible,
        isImportingHistory,
        sendingHistory,
        tab,
        onCopyInvoiceID,
        reloadNumOpen,
        loadingUploadStats,
        uploadStats,
        onUploadInvoice,
        hasAPIConnection,
        loadingMore
    } = props;
    const filterRef = useRef<InvoiceFilterRef>(null);
    const introBannerConfig = useMemo(() => {
        if (tab === DashboardTab.INVOICE_REQUESTS) {
            return {
                title: t('dashboard.introBanner.requestsTitle', 'Factuur aanvragen'),
                featureKey: 'invoice_requests',
                message: t(
                    'dashboard.introBanner.requestsMessage',
                    'Upload de facturen automatisch naar bol. Wees factuuraanvragen vóór.'
                ),
                variant: 'info' as const
            };
        }
        return {
            title: t('dashboard.introBanner.invoicesTitle', 'Facturen'),
            featureKey: 'invoices',
            message: t(
                'dashboard.introBanner.invoicesMessage',
                'Beheer jouw orders en facturen op één plek, zodat je altijd overzicht hebt.'
            ),
            variant: 'info' as const
        };
    }, [tab, t]);

    // Grouping formats a date per invoice — memoize so keystrokes in the
    // search field (which re-render the dashboard) don't re-run it.
    const invoiceGroups = useMemo(() => groupInvoicesByDate(invoices), [invoices]);

    const activeFilterCount = useMemo(() => {
        let count = 0;
        for (const [key, value] of Object.entries(filter)) {
            if (key === 'customer_type') {
                if (value !== CustomerType.BOTH) count += 1;
            } else if (value) count += 1;
        }
        return count;
    }, [filter]);
    const filterLabel = !activeFilterCount
        ? null
        : activeFilterCount === 1
          ? t('dashboard.filters.oneActive', '1 actieve filter')
          : `${activeFilterCount} ${t('dashboard.filters.manyActive', 'actieve filters')}`;

    function onResetFilter() {
        filterRef.current?.onReset();
    }

    function getInvoiceOptionsMenu(invoice: Invoice): BolmateMenuOption[] {
        const invoiceOptions = getInvoiceOptions(invoice, context.userContext.user).filter((option: InvoiceOption) =>
            userHasRight(context, option.accessKey, invoice.accountId)
        );
        const options: Array<BolmateMenuOption> = [];
        for (const option of invoiceOptions) {
            let onClick: MouseEventHandler | undefined = undefined;
            const optionType = option.type;
            if (optionType === InvoiceOptionName.CREDIT) onClick = onClickCredit(invoice);
            else if (optionType === InvoiceOptionName.DETAILS) onClick = onClickDetails(invoice);
            else if (optionType === InvoiceOptionName.DOWNLOAD) onClick = onClickDownload(invoice);
            else if (optionType === InvoiceOptionName.EDIT) onClick = onClickEdit(invoice);
            else if (optionType === InvoiceOptionName.PREVIEW) onClick = onPreviewFile(invoice);
            else if (optionType === InvoiceOptionName.SEND_INVOICE) onClick = onClickMail(invoice);
            else if (optionType === InvoiceOptionName.UPLOAD_INVOICE) onClick = onUploadInvoice(invoice);
            else if (optionType === InvoiceOptionName.REVERSE_CHARGE) onClick = onClickReverseCharge(invoice);
            else if (optionType === InvoiceOptionName.SET_NUMBER) onClick = onClickAssignInvoiceNumber(invoice);
            else if (optionType === InvoiceOptionName.CANCEL_EMAILS) onClick = onCancelEmails(invoice);
            else if (optionType === InvoiceOptionName.COPY_ID) onClick = onCopyInvoiceID(invoice);
            if (onClick)
                options.push({
                    onClick,
                    icon: option.icon,
                    label: option.label,
                    disabled: option.disabled
                });
        }
        return options;
    }

    function renderInvoicesContainer() {
        const isAdmin = context.userContext.user?.isSuperAdmin() || hasAdminLogin();
        return (
            <div className='invoices-column'>
                {tab === DashboardTab.INVOICE_REQUESTS && (
                    <InvoiceRequestController reloadNumOpen={reloadNumOpen} stats={uploadStats} />
                )}
                {tab === DashboardTab.INVOICES && isImportingHistory && renderImportState()}
                {tab === DashboardTab.INVOICES && !isImportingHistory && (
                    <>
                        <div className='filter-container'>
                            <div className='title-row'>
                                <label className='col-title'>{t('dashboard.orders', 'Orders')}</label>
                                {isAdmin && (
                                    <BolmateButton
                                        onClick={onReload}
                                        label={
                                            <AwesomeIcon
                                                name='rotate'
                                                className={classNames('reload-icon', loadingInvoices && 'spinning')}
                                            />
                                        }
                                        tertiary
                                        shape='square'
                                        size='small'
                                        className='reload-btn'
                                    />
                                )}
                            </div>
                            {hasAPIConnection && (
                                <div className='search-container'>
                                    {renderLeftSearchContainer()}
                                    {renderRightSearchContainer()}
                                </div>
                            )}
                        </div>
                        {renderInvoices()}
                        {loadingMore && (
                            <div className='load-more-container'>
                                <DotsLoader dotSize='3rem' />
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    }

    function renderImportState() {
        return (
            <BolmateEmptyState
                ornament={<DotsLoader dotSize='5rem' />}
                title={t('dashboard.importOverlay.title', 'De orders van de afgelopen 30 dagen worden geïmporteerd')}
                className='import-state'
            >
                {t('dashboard.importOverlay.line1', 'We sturen je een e-mail zodra het importeren voltooid is.')}
                <br />
                {t('dashboard.importOverlay.line2', 'Je kunt ondertussen jouw account naar wens instellen.')}
            </BolmateEmptyState>
        );
    }

    function renderInvoices() {
        return (
            <div className='invoices'>
                <Loader active={loadingInvoices} />
                {hasAPIConnection && <ImportStatus />}
                {renderInvoiceRows()}
            </div>
        );
    }

    function renderInvoiceRows() {
        const {account} = context.accountContext,
            status = account?.status;
        if (invoices.length) return invoiceGroups.map(renderInvoiceGroup);
        else if (activeFilterCount) return renderInvoicesNotFound();
        else if (status === AccountStatus.IMPORTING) return renderImportingStatus();
        else if (!hasAPIConnection) return renderConnectionError();
        else return renderEmptyInvoices();
    }

    function renderInvoiceGroup(group: InvoiceDateGroup) {
        return (
            <BolmateListGroup key={group.key} title={group.date.format('DD MMM YYYY')} className='order-date-group'>
                {group.invoices.map(renderInvoice)}
            </BolmateListGroup>
        );
    }

    function renderEmptyInvoices() {
        return (
            <BolmateEmptyState image='empty-dashboard' title={t('dashboard.empty.title', 'Nog geen orders ontvangen')}>
                {t(
                    'dashboard.empty.subtitle',
                    'Zodra we de eerste order van Bol ontvangen wordt deze hier getoond. Je kunt via de zoekbalk ook oudere orders opzoeken, dit kan tot 2 jaar terug!'
                )}
            </BolmateEmptyState>
        );
    }

    function renderConnectionError() {
        return (
            <BolmateEmptyState
                image='connect-api'
                title={t('dashboard.connectionError.welcome', 'Welkom in de Bolmate app!')}
                className='connection-error'
            >
                {t('dashboard.connectionError.willShow', 'In dit scherm zie je straks je opgehaalde orders.')}{' '}
                {t(
                    'dashboard.connectionError.connectPrompt',
                    'Verbind Bolmate met Bol om jouw orders automatisch te kunnen ophalen.'
                )}{' '}
                {t('dashboard.connectionError.nothingSent', 'We versturen nog niks.')}
                <div className='connect-action'>
                    <BolmateButton
                        onClick={onClickApiFlow}
                        label={t('dashboard.connectWithBol', 'Koppelen met Bol')}
                        ornaments={{element: <AwesomeIcon name='link' />}}
                    />
                </div>
            </BolmateEmptyState>
        );
    }

    function renderInvoicesNotFound() {
        return (
            <BolmateEmptyState image='data-rows' title={t('dashboard.notFound.title', 'Geen orders gevonden')}>
                {t(
                    'dashboard.notFound.tipBefore',
                    'Tip: voor zoeken op achternaam, bedrijf, factuurnummer etc. Gebruik'
                )}{' '}
                <Link onClick={onOpenFilters}>{t('dashboard.advancedSearch', 'Uitgebreid zoeken')}</Link>.
            </BolmateEmptyState>
        );
    }

    function renderImportingStatus() {
        return (
            <div className='importing-container'>
                <Loader active disableBackground position='relative' className='importing-loader' />
                <div className='labels'>
                    <label className='main'>{t('dashboard.importing.fetching', 'Orders aan het ophalen')}</label>
                    <label>{t('dashboard.importing.firstTime', 'De eerste keer kan dit enkele minuten duren.')}</label>
                    <label>{t('dashboard.importing.checkStatus', 'Check de status rechts bovenin de pagina.')}</label>
                </div>
            </div>
        );
    }

    function getAccountName(accountId: string): string | null {
        for (const account of context.accountContext.accounts) {
            if (account.id === accountId) return account.selectablePlatformLabel;
        }
        return null;
    }

    function renderInvoice(invoice: Invoice) {
        const fromActiveAccount = activeAccountId === invoice.accountId;
        const accountName = fromActiveAccount ? null : getAccountName(invoice.accountId);
        return (
            <OrderCard
                key={invoice.id}
                invoice={invoice}
                fromActiveAccount={fromActiveAccount}
                accountName={accountName}
                options={() => getInvoiceOptionsMenu(invoice)}
                onClickAssignInvoiceNumber={onClickAssignInvoiceNumber}
                onVisible={onCardVisible(invoice.getTempField('ix'))}
            />
        );
    }

    function onChangeSearchInput(value: string) {
        // Parity with the previous input's allowCharReg: order numbers are
        // alphanumeric only.
        onChangeSearch(value.replace(/[^A-Za-z0-9]/g, ''));
    }

    function onSearchKeyDown(evt: React.KeyboardEvent<HTMLInputElement>) {
        if (evt.key === 'Enter') onSearch(evt);
    }

    function renderLeftSearchContainer() {
        return (
            <div className='left'>
                <BolmateSearchInput
                    onChange={onChangeSearchInput}
                    value={filter.order_id ?? ''}
                    onKeyDown={onSearchKeyDown}
                    className='search-field'
                    placeholder={t('dashboard.filters.searchPlaceholder', 'Zoek op ordernummer')}
                    // Drop invalid characters at the field itself — the
                    // sanitized value can equal the current filter, in which
                    // case no re-render would snap the field back.
                    allowCharReg={/[A-Za-z0-9]/}
                />
                <div className='filters-container'>
                    <BolmateButton
                        onClick={onOpenFilters}
                        label={t('dashboard.advancedSearch', 'Uitgebreid zoeken')}
                        tertiary
                        shape='square'
                        align='center'
                    />
                    {renderActiveFilters()}
                    <InvoiceFilterController
                        onSubmit={onSubmitFilters}
                        onClose={onCloseFilters}
                        active={filtersOpen}
                        filter={filter}
                        ref={filterRef}
                    />
                </div>
                {renderSendingHistory()}
            </div>
        );
    }

    function renderActiveFilters() {
        // Always rendered — an invisible placeholder reserves the label's
        // space so the top bar doesn't shift when filters (de)activate.
        return (
            <div className={classNames('active-filters-label', !filterLabel && 'placeholder')}>
                <label className='filter-count' onClick={onOpenFilters}>
                    {filterLabel ?? t('dashboard.filters.oneActive', '1 actieve filter')}
                </label>{' '}
                -{' '}
                <span onClick={onResetFilter} className='delete-filters-btn'>
                    {t('dashboard.filters.removeFilters', 'Verwijder x')}
                </span>
            </div>
        );
    }

    function renderRightSearchContainer() {
        const statusItems = getOrderStatusItems(t('dashboard.filters.statusAll', 'Alle statussen'));
        return (
            <div className='right'>
                <PortalSelectController
                    onChange={(value: string | null) => onChangeStatusFilter((value ?? '') as OrderStatus | '')}
                    value={filter.order_status ?? ''}
                    items={statusItems}
                    shape='square'
                    className='order-status-select'
                />
            </div>
        );
    }

    function renderSendingHistory() {
        if (!sendingHistory) return null;
        return (
            <div className='sending-history-label'>
                <AwesomeIcon name='paper-plane' className='plane-icon' />
                <label>{t('dashboard.bulk.sendingHistory', 'Bulk e-mails worden ingepland')}</label>
            </div>
        );
    }

    return (
        <>
            <InfoBanner
                featureKey={introBannerConfig.featureKey}
                title={introBannerConfig.title}
                variant={introBannerConfig.variant}
                showForNewUsersOnly={true}
            >
                {introBannerConfig.message}
            </InfoBanner>
            <div className='dashboard'>
                {/* PWA-16: the summary + upload stat tiles are hidden on mobile
                    (better tiles land in v2); the setup to-do list is hidden with
                    them, so the orders list starts right under the header. */}
                {tab === DashboardTab.INVOICE_REQUESTS && !bp.isMobileLayout && (
                    <UploadStatsController stats={uploadStats} loading={loadingUploadStats} />
                )}
                {renderInvoicesContainer()}
            </div>
        </>
    );
}
